import {
  DateInput,
  DateInputProps,
  DateInputStylesNames,
  TimeInput,
} from '@mantine/dates';
import stylesColors from 'common/styles/styles.colors';
import Separator from 'components/common/separator';
import { FullContainer, HalfContainer } from 'components/styles';
import { format } from 'date-fns';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { RowBetweenStyles } from 'legacy-modules/styles';
import * as React from 'react';
import { useController, useFormContext, useWatch } from 'react-hook-form';

import CustomFormControl from '../custom-form-control';
import { BaseElementInputProps } from '../field-type';
import { FormContext } from '../form';
import ReadOnlyText from '../read-only-text';
import { styles } from '../styles';

import 'dayjs/locale/id';

dayjs.locale('id');
dayjs.extend(customParseFormat);

export interface DateTimePickerInputProps
  extends BaseElementInputProps,
    // Omit<
    //   CustomFormControlProps,
    //   'name' | 'overrides' | 'positive' | 'error' | 'children'
    // >,
    Omit<DateInputProps, 'name'> {
  type: 'date-time';
  fieldType?: 'control' | 'uncontrol';
  hasTime?: boolean;
  noMargin?: boolean;
  onAfterChange?: (date: Date | null) => void;
  hideLabel?: boolean;
  hideRequiredStar?: boolean;
  overrides?: {
    [key in DateInputStylesNames]?: React.CSSProperties;
  };
}

export default function DateTimePickerInput(props: DateTimePickerInputProps) {
  const { control } = useFormContext<any>();
  const value = useWatch({ control, name: props.name });
  const { field, fieldState } = useController({ name: props.name });
  const context = React.useContext(FormContext);
  const {
    minDate = new Date('1900-01-01T17:00:00.000Z'),
    readOnly,
    required,
    hideLabel,
    label = '',
    hideRequiredStar,
    valueFormat = 'DD/MM/YYYY',
    clearable = true,
    fieldType = 'control',
    hasTime,
    noMargin,
    size,
    onAfterChange,
    overrides,
    ...restProps
  } = props;

  const ref = React.useRef<HTMLInputElement>(null);

  const dateParser = (value) => dayjs(value, valueFormat).toDate();

  const timeParser = (value) =>
    value.toLocaleTimeString([], {
      hour12: false,
      hour: '2-digit',
      minute: '2-digit',
    });

  const _readOnlyValue = React.useMemo(() => {
    if (value) {
      if (Array.isArray(value)) {
        return value.map((cur) => format(cur, 'dd/MM/yyyy')).join(' - ');
      } else {
        return format(value, 'dd/MM/yyyy');
      }
    }

    return undefined;
  }, [value]);

  if (readOnly) {
    return (
      <ReadOnlyText
        required={required}
        hideLabel={hideLabel}
        label={label}
        value={_readOnlyValue}
      />
    );
  }
  const shouldShowError = !!fieldState.error?.message;

  const disabled = !context.editable || restProps.disabled;

  const _value = fieldType === 'control' ? field.value : value;

  const component = (
    <DateInput
      {...field}
      {...restProps}
      radius="md"
      popoverProps={{
        withinPortal: true,
      }}
      value={_value}
      placeholder={valueFormat}
      valueFormat={valueFormat}
      dateParser={dateParser}
      size="xs"
      styles={{
        label: {
          fontSize: 14,
          ':disabled': styles.reset,
          ...overrides?.label,
        },
        input: {
          fontSize: 16,
          ':disabled': {
            ...styles.reset,
            opacity: 1,
            color: stylesColors.inkDark,
          },
          ...overrides?.input,
        },
        ...overrides,
      }}
      minDate={minDate}
      onChange={(date) => {
        field.onChange(date);
        onAfterChange?.(date);
      }}
      required={required}
      disabled={disabled}
      error={shouldShowError}
      clearable={clearable}
    />
  );

  return (
    <CustomFormControl
      required={required}
      hideLabel={hideLabel}
      label={label}
      hideRequiredStar={hideRequiredStar}
      error={fieldState.error?.message}
      disabled={disabled}
    >
      <FullContainer $style={RowBetweenStyles}>
        {hasTime ? (
          <HalfContainer>{component}</HalfContainer>
        ) : (
          <FullContainer>{component}</FullContainer>
        )}
        {hasTime && (
          <>
            <Separator gap={8} />
            <HalfContainer>
              <TimeInput
                {...field}
                ref={ref}
                value={timeParser(_value)}
                onChange={(e) => {
                  const time = e.target.value;
                  const [hour, minute] = time
                    .split(':')
                    .map((v) => parseInt(v, 10));

                  const newValue = new Date(_value);
                  newValue.setHours(hour, minute);

                  field.onChange(newValue);
                  onAfterChange?.(newValue);
                }}
                error={shouldShowError}
                disabled={disabled || readOnly}
                required={required}
                styles={{
                  root: {
                    ...(disabled ? styles.reset : styles.active),
                    maxHeight: '30px',
                  },
                  wrapper: {
                    ...(disabled ? styles.reset : styles.active),
                    maxHeight: '30px',
                  },
                  input: {
                    ...(disabled ? styles.reset : styles.active),
                    borderRadius: '8px',
                    maxHeight: '30px',
                    minHeight: 0,
                    fontSize: '16px',
                    opacity: '1 !important',
                  },
                }}
              />
            </HalfContainer>
          </>
        )}
      </FullContainer>
    </CustomFormControl>
  );
}
